/**
 * Token Helper Utilities
 * Provides functions to safely get and validate tokens from localStorage
 */

/**
 * Get token from localStorage and sanitize it
 * Removes quotes, trims whitespace, and validates format
 */
export const getToken = (): string | null => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return null;
    }
    
    // Remove quotes if present (sometimes tokens are stored with quotes)
    let sanitized = token.replace(/^["']|["']$/g, '');
    sanitized = sanitized.trim();
    
    // Check if token is the string "null" or "undefined"
    if (sanitized === 'null' || sanitized === 'undefined' || sanitized === '') {
      // Clean up invalid token
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return null;
    }
    
    // Validate JWT format (should have 3 parts separated by dots)
    const parts = sanitized.split('.');
    if (parts.length !== 3) {
      // Invalid token format - clean it up
      console.warn('Invalid token format detected, removing token');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return null;
    }
    
    return sanitized;
  } catch (error) {
    console.error('Error getting token from localStorage:', error);
    return null;
  }
};

/**
 * Store token in localStorage safely
 */
export const setToken = (token: string): void => {
  try {
    // Validate token format before storing
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid token format');
    }
    
    // Remove any existing quotes and trim
    const sanitized = token.replace(/^["']|["']$/g, '').trim();
    localStorage.setItem('token', sanitized);
  } catch (error) {
    console.error('Error storing token:', error);
    throw error;
  }
};

/**
 * Remove token from localStorage
 */
export const removeToken = (): void => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  } catch (error) {
    console.error('Error removing token:', error);
  }
};

/**
 * Check if token exists and is valid format
 */
export const hasValidToken = (): boolean => {
  const token = getToken();
  return token !== null && token !== '';
};

